import { WarningAmber } from "@mui/icons-material";
import {
	Box,
	MenuItem,
	Skeleton,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";

import {
	formatDuration,
	formatExampleTime,
	formatSkipGapOption,
	isSkipGapInvalid,
} from "./utils";

interface ISkipGapExceptionProps {
	newShiftSettings: any;
	setNewShiftSettings: any;
	isLoading: boolean;
}

const titleSx = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "16px",
	color: "rgba(0, 0, 0, 0.87)",
};

const helperSx = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "12px",
	lineHeight: 1.66,
	color: "rgba(0, 0, 0, 0.6)",
};

const ERROR_COLOR = "#E44B48";

const SKIP_GAP_MINUTES_OPTIONS = [0, 30, 60, 90, 120];

const SkipGapException = ({
	newShiftSettings,
	setNewShiftSettings,
	isLoading,
}: ISkipGapExceptionProps) => {
	const gapHours = Number(newShiftSettings.minShiftsGapHours ?? 0);
	const skipMinutes = Number(newShiftSettings.minShiftsGapSkipMinutes ?? 0);
	const isSwitchDisabled = gapHours <= 0;
	const isSkipEnabled =
		!isSwitchDisabled && (newShiftSettings.minShiftsGapSkipEnabled ?? false);
	const isInvalid = isSkipGapInvalid(newShiftSettings);

	// Keep the saved value listed even when it is no longer valid, so the
	// error can be shown against it.
	const options = useMemo(() => {
		const validOptions = SKIP_GAP_MINUTES_OPTIONS.filter(
			(minutes) => minutes < gapHours * 60
		);
		return validOptions.includes(skipMinutes)
			? validOptions
			: [...validOptions, skipMinutes].sort((a, b) => a - b);
	}, [gapHours, skipMinutes]);

	const handleSwitchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setNewShiftSettings((prevSettings: any) => ({
				...prevSettings,
				minShiftsGapSkipEnabled: e.target.checked,
			}));
		},
		[setNewShiftSettings]
	);

	const handleMinutesChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setNewShiftSettings((prevSettings: any) => ({
				...prevSettings,
				minShiftsGapSkipMinutes: Number(e.target.value),
			}));
		},
		[setNewShiftSettings]
	);

	const skipUntil = formatExampleTime(skipMinutes);
	const gapEnd = formatExampleTime(gapHours * 60);

	return (
		<Box
			sx={{
				marginTop: "24px",
				paddingTop: "24px",
				borderTop: "1px solid rgba(0, 0, 0, 0.12)",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Typography sx={titleSx}>
						Allow Workers to pick up Shifts that start right after (or end right
						before) their Shift
					</Typography>
					<Typography sx={{ ...helperSx, textWrap: "pretty" }}>
						{isSwitchDisabled
							? "Set a gap of 1 hour or more above to turn this on."
							: "The minimum gap is skipped when the Shifts are this close together. Shifts further apart than this, but closer than the minimum gap, stay hidden from the Worker."}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{isLoading ? (
						<Skeleton width={50} height={40} />
					) : (
						<Switch
							checked={isSkipEnabled}
							disabled={isSwitchDisabled}
							onChange={handleSwitchChange}
							inputProps={{
								"aria-label":
									"Allow Workers to pick up Shifts that start right after (or end right before) their Shift",
							}}
						/>
					)}
				</Box>
			</Box>
			{isSkipEnabled && (
				<Box sx={{ marginTop: "24px" }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "8px",
						}}
					>
						<Typography sx={titleSx}>
							Skip the minimum gap when the Shifts are at most
						</Typography>
						{isLoading ? (
							<Skeleton width={200} height={55} />
						) : (
							<TextField
								id='skip-gap-max-minutes'
								select
								variant='filled'
								hiddenLabel
								error={isInvalid}
								value={skipMinutes}
								onChange={handleMinutesChange}
								slotProps={{
									htmlInput: {
										"aria-label": "Maximum time between Shifts",
									},
								}}
								sx={{
									width: "200px",
									"& .MuiFilledInput-root": {
										height: "40px",
										background: "#FFF",
									},
									"& .MuiFilledInput-root:hover, & .MuiFilledInput-root.Mui-focused":
										{
											background: "#FFF",
										},
									"& .MuiSelect-select": {
										padding: "8px",
										background: "#FFF",
									},
									"& .MuiFilledInput-root.Mui-error:after, & .MuiFilledInput-root.Mui-error:before":
										{
											borderBottomColor: ERROR_COLOR,
										},
								}}
							>
								{options.map((minutes) => (
									<MenuItem key={minutes} value={minutes}>
										{formatSkipGapOption(minutes)}
									</MenuItem>
								))}
							</TextField>
						)}
						<Typography sx={titleSx}>apart</Typography>
					</Box>
					{isInvalid ? (
						<Box
							role='alert'
							sx={{
								marginTop: "8px",
								display: "flex",
								alignItems: "center",
								gap: "4px",
							}}
						>
							<WarningAmber sx={{ fontSize: "14px", color: ERROR_COLOR }} />
							<Typography sx={{ ...helperSx, color: ERROR_COLOR }}>
								Must be lower than the minimum gap (
								{formatDuration(gapHours * 60)}). Pick a shorter value or
								raise the gap.
							</Typography>
						</Box>
					) : (
						<Typography sx={{ ...helperSx, marginTop: "8px" }}>
							Example: a Worker whose Shift ends at 2:00 PM can pick up a Shift
							starting by <b>{skipUntil}</b>. Shifts starting after{" "}
							<b>{skipUntil}</b> and before <b>{gapEnd}</b> are hidden; from{" "}
							<b>{gapEnd}</b> they are shown as usual.
						</Typography>
					)}
				</Box>
			)}
		</Box>
	);
};

export default SkipGapException;
